"use client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Alert,
  Avatar,
  FormControlLabel,
  Checkbox,
  alpha,
  useTheme,
} from "@mui/material";
import {
  VisibilityTwoTone,
  VisibilityOffTwoTone,
  PersonAddTwoTone,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import apiEndpoints from "../../apiConfig";

function SignupForm({ onToggleMode }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { setUser } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    agreeTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setshowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // Check for empty fields
    if (
      !formData.name ||
      !formData.email ||
      !formData.phone ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      setError("Please fill in all fields");
      return;
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    // Phone validation (10–12 digits)
    const phoneRegex = /^\d{10,12}$/;
    if (!phoneRegex.test(formData.phone)) {
      setError("Phone number must be 10–12 digits only.");
      return;
    }
    // Password validation:
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{7,}$/;
    if (!passwordRegex.test(formData.password)) {
      setError(
        "Password must be at least 7 characters, include one uppercase, one lowercase, and one special character.",
      );
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!formData.agreeTerms) {
      setError(
        "Please agree to the Terms & Conditions and Privacy Policy before continuing.",
      );
      return;
    }

    const safeFetchJson = async (url, options = {}) => {
      const response = await fetch(url, options);
      const text = await response.text();
      if (!response.ok) {
        throw new Error(
          `Server Error (${response.status}): ${text.substring(0, 100) || "Empty response"}`,
        );
      }
      try {
        return JSON.parse(text);
      } catch (e) {
        console.error("Failed to parse JSON:", text);
        throw new Error("Invalid server response");
      }
    };

    try {
      const emailData = await safeFetchJson(
        `${apiEndpoints.checkUser}?action=check_email&email=${encodeURIComponent(
          formData.email,
        )}`,
      );
      if (emailData.status === "exists") {
        setError("Email already exists.");
        return;
      }

      const phoneData = await safeFetchJson(
        `${apiEndpoints.checkUser}?action=check_phone&phone=${encodeURIComponent(
          formData.phone,
        )}`,
      );
      if (phoneData.status === "exists") {
        setError("Phone number already exists.");
        return;
      }

      const formPayload = new FormData();
      formPayload.append("name", formData.name);
      formPayload.append("email", formData.email);
      formPayload.append("phone", formData.phone);
      formPayload.append("password", formData.password);

      const data = await safeFetchJson(
        `${apiEndpoints.register}?action=insert`,
        {
          method: "POST",
          body: formPayload,
        },
      );

      if (data.status === "success") {
        const userObj = {
          id: data.id,
          user_id: data.user_id,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          isVerified: 0,
        };
        localStorage.setItem("matrimony_user", JSON.stringify(userObj));
        localStorage.setItem("id", data.id);
        localStorage.setItem("user_id", data.user_id);
        setUser(userObj);
        navigate("/edit-profile", { state: { user_id: data.user_id } });
      } else {
        setError(data.message || "Failed to create account");
      }
    } catch (error) {
      setError(error.message || "Failed to connect to server");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },

        width: {
          xs: "90%",
          sm: "85%",
          md: "80%",
          lg: "100%",
          xl: "100%",
        },

        maxWidth: { xs: 400, sm: 500, md: 800, lg: 1000, xl: 1200 },

        height: {
          xs: "auto",
          sm: "auto",
          md: 450,
          lg: 550,
          xl: 550,
        },

        minHeight: { xs: 500, sm: 550 },

        borderRadius: 4,
        overflow: "hidden",

        mx: "auto",
        my: "auto",

        bgcolor: "background.paper",

        boxShadow: theme.shadows[2], // optional container shadow
      }}
    >
      {/* Left - Form */}
      <CardContent
        sx={{
          flex: 1,
          p: {
            xs: 3,
            sm: 3, // Reduced padding on small screens
            md: 4,
            lg: 5,
          },
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start", // Changed from center to start
          minWidth: { md: 350, lg: 400 },
          overflow: "auto", // Enable scrolling if content overflows
          "&::-webkit-scrollbar": {
            display: "none", // Hide scrollbar for cleaner look
          },
        }}
      >
        <Box textAlign="center" mb={2}>
          {" "}
          {/* Reduced margin */}
          <Avatar
            sx={{
              width: 50,
              height: 50,
              mx: "auto",
              mb: 1.5,
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
              color: "secondary.main", // Gold
            }}
          >
            <PersonAddTwoTone fontSize="medium" />
          </Avatar>
          <Typography
            variant="h5"
            fontWeight={700}
            color="primary.main"
            mb={0.5}
            fontSize={{ xs: "1.1rem", sm: "1.3rem", md: "1.5rem" }}
          >
            Join Matrimony
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            fontSize={{ xs: "0.75rem", sm: "0.8rem", md: "0.875rem" }} // Smaller on mobile
            mb={2} // Added margin to separate from form
          >
            Create your account to start your journey
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, sm: 2 }, // Reduced gap on mobile
            flex: 1,
            overflow: "auto",
            paddingTop: 1,
          }}
        >
          <TextField
            fullWidth
            label="Full Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            size="small" // Smaller size on all screens
          />
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            size="small"
          />
          <TextField
            fullWidth
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            size="small"
          />
          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleChange}
            required
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small" // Smaller icon button
                  >
                    {showPassword ? (
                      <VisibilityOffTwoTone />
                    ) : (
                      <VisibilityTwoTone />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setshowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    size="small"
                  >
                    {showConfirmPassword ? (
                      <VisibilityOffTwoTone />
                    ) : (
                      <VisibilityTwoTone />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert
              severity="error"
              variant="filled"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                py: 0.5,
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.agreeTerms}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    agreeTerms: e.target.checked,
                  }))
                }
                size="small"
                color="secondary"
              />
            }
            label={
              <Typography
                variant="body2"
                fontSize={{ xs: "0.75rem", sm: "0.8rem" }}
                color="text.secondary"
              >
                I agree to the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  Terms & Conditions
                </a>{" "}
                and{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  Privacy Policy
                </a>
              </Typography>
            }
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{
              mt: 1,
              py: { xs: 1, sm: 1.25 },
              fontSize: { xs: "0.85rem", sm: "0.9rem" },
            }}
            disabled={isLoading || !formData.agreeTerms}
          >
            {isLoading ? "Creating Account..." : "Create Account"}
          </Button>
        </Box>

        {/* Login toggle - positioned at bottom */}
        <Box sx={{ mt: 2, textAlign: "center", pt: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary" }}
            fontSize={{ xs: "0.75rem", sm: "0.8rem" }}
          >
            Already have an account?{" "}
            <Button
              variant="text"
              onClick={onToggleMode}
              sx={{
                p: 0,
                textTransform: "none",
                color: "secondary.main", // Gold
                textDecoration: "underline",
                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                minWidth: "auto",
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "transparent",
                },
              }}
            >
              Login here
            </Button>
          </Typography>
        </Box>
      </CardContent>

      {/* Right - Image */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          justifyContent: "center",
          alignItems: "center",
          p: 2,
          bgcolor: "background.default",
        }}
      >
        <Box
          component="img"
          src="chimma.jpg"
          alt="Wedding rings on a veil"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: 3,
            boxShadow: theme.shadows[4],
          }}
        />
      </Box>
    </Card>
  );
}

export default SignupForm;
